/**
 * Operations › Fleet health (docs/CONTRACT-fleet-governance.md sections 4 to 6 and 12.2): the wire types, written from the
 * contract and never imported from the service; the calls to /v1/admin/fleet/*; the closed words people read (statuses,
 * problem kinds, OTA eligibility sentences); the export link; the settings form; and the probe that offers the page only
 * where the service has the `fleet` flag on (every route answers 404 `fleet_disabled` while it is off).
 *
 * Fleet is identified (contract 1.5): device id, computer name and owner e-mail, shown to IT admins and auditors only.
 * It never holds a question, an answer, an intent or a trace.
 */
import { useEffect, useState } from 'react';
import { errorText } from './errors';
import { adminGet, adminSend, queryOf, statusOf } from './fleetHttp';
import { FALLBACK_CATALOG, valueText, type SwitchSource, type SwitchValue } from './pcSwitches';

// ------------------------------------------------------------------------------------------------ the record (section 4.2)

export type Ring = 'canary' | 'early' | 'broad';
export const RINGS: readonly Ring[] = ['canary', 'early', 'broad'];
export const FLEET_STATUSES = ['online', 'recent', 'stale', 'not-seen', 'gone'] as const;
export type FleetStatus = (typeof FLEET_STATUSES)[number];
/** The closed list, in the order every list and filter shows them. */
export const FLEET_PROBLEM_KINDS = [
  'behind-version', 'crashed', 'webview2-failing', 'mcp-off', 'sign-in-failing', 'not-seen', 'push-flapping',
  'policy-behind', 'quarantined', 'disabled', 'update-refused', 'update-ineligible', 'version-disagrees',
] as const;
export type FleetProblemKind = (typeof FLEET_PROBLEM_KINDS)[number];
export type VersionSource = 'health' | 'switches' | 'logs';
export const MCP_STATUSES = ['healthy', 'unhealthy', 'unknown', 'disabled', 'blocked', 'consent-required', 'unsupported', 'stale'] as const;
export type McpStatus = (typeof MCP_STATUSES)[number];
export type { SwitchSource } from './pcSwitches';
/** A switch's key (desktopPolicyCatalog.ts SwitchKey). */
export type SwitchKey = string;
/** The switches one level sets (desktopPolicyCatalog.ts PartialSwitches): a key that is absent is not set there. */
export type PartialSwitches = Record<SwitchKey, SwitchValue>;
export type OtaIneligibilityReason =
  | 'health_missing' | 'health_stale' | 'unhealthy' | 'crash_count_nonzero' | 'ring_ineligible'
  | 'installed_version_invalid' | 'not_an_upgrade' | 'not_a_rollback' | 'below_min_from_version';

/** One entry of a PC's log ring (desktopLogs.ts DesktopLogEntry). */
export interface DesktopLogEntry {
  atMs: number;
  level: 'warn' | 'error' | 'critical';
  category: string;
  message: string;
  exception?: string;
  source: 'agent' | 'widget' | 'dump';
}

export interface FleetKey {
  fingerprint: string;
  purpose: 'user' | 'broker-audit';
  status: 'active' | 'revoked';
  owner: { oid: string; email: string } | null;
  enrolledAt: number;
  lastEnrolledAt: number;
  revokedAt: number | null;
}

export interface FleetMcpServer {
  server: string;
  displayName: string;
  status: McpStatus;
  errorCode: string | null;
  uptimePercent24h: number | null;
  toolCount: number;
}

export interface FleetRecord {
  deviceId: string;
  computerName: string | null;
  owner: { oid: string | null; email: string | null } | null;
  keys: FleetKey[];
  enrolledAt: number | null;
  lastEnrolledAt: number | null;
  agentVersion: string | null;
  versionSeenAt: number | null;
  versionSource: VersionSource | null;
  versions: Array<{ source: VersionSource; version: string; at: number }>;
  ring: Ring | null;
  ringSource: 'health' | 'switches' | null;
  switchesRing: { ring: Ring; source: SwitchSource } | null;
  health: { at: number; healthy: boolean; crashCount: number | null; lastError: string | null } | null;
  switches: {
    reportedAt: number;
    revisionApplied: number;
    revisionCurrent: number;
    effective: PartialSwitches;
    sources: Partial<Record<SwitchKey, SwitchSource>>;
    differsFromFleet: SwitchKey[];
    mcpOff: boolean;
    quarantined: boolean;
    disabled: boolean;
  } | null;
  logs: {
    updatedAt: number;
    total: number;
    errors24h: number; warns24h: number; critical24h: number; dumps24h: number;
    webview2Failures24h: number; signInFailures24h: number;
    latest: DesktopLogEntry[];
  } | null;
  push: {
    connectedNow: boolean;
    lastConnectedAt: number | null;
    drops24h: number | null;
    lastRefused: { type: string; reason: string; at: number } | null;
  };
  ota: {
    lastOffered: { version: string; proposalId: string; at: number; rollback: boolean } | null;
    lastAck: { applied: boolean; at: number | null } | null;
    eligibility: OtaIneligibilityReason[] | null;
  };
  mcp: { at: number; servers: FleetMcpServer[] } | null;
  lastSeenAt: number | null;
  status: FleetStatus;
  problems: FleetProblemKind[];
}

// ------------------------------------------------------------------------------------------------ settings (section 6)

export interface FleetSettings {
  recentHours: number;
  staleHours: number;
  goneDays: number;
  deviceRetentionDays: number;
  pushFlappingDrops: number;
  policyBehindMinutes: number;
  currentVersionOverride: Record<Ring, string | null>;
  notifyIt: boolean;
  alert: { crashesPerPc7d: number; minPcsPerVersion: number; darkPcs: number };
}

/** The strict partial patch the PUT takes: nested objects patched key by key. */
export type FleetSettingsPatch = Partial<Omit<FleetSettings, 'currentVersionOverride' | 'alert'>> & {
  currentVersionOverride?: Partial<Record<Ring, string | null>>;
  alert?: Partial<FleetSettings['alert']>;
};

export const FLEET_SETTINGS_DEFAULTS: FleetSettings = {
  recentHours: 13,
  staleHours: 72,
  goneDays: 30,
  deviceRetentionDays: 180,
  pushFlappingDrops: 12,
  policyBehindMinutes: 60,
  currentVersionOverride: { canary: null, early: null, broad: null },
  notifyIt: false,
  alert: { crashesPerPc7d: 0.5, minPcsPerVersion: 5, darkPcs: 5 },
};

export type FleetJobKey = 'tick' | 'snapshot' | 'ota-scan' | 'sweep' | 'alerts';

export interface FleetSettingsView {
  settings: FleetSettings;
  defaults: FleetSettings;
  flag: true;
  currentVersions: Record<Ring, { version: string | null; source: 'override' | 'ota' | null; proposalId: string | null; publishedAt: number | null }>;
  jobs: Record<FleetJobKey, { lastRunAt: number; lastResult: string; lastError?: string }>;
}

// ------------------------------------------------------------------------------------------------ the route answers (section 5)

export interface FleetMcpSummary {
  server: string; displayName: string; pcs: number; healthy: number; unhealthy: number; other: number;
  topErrorCode: string | null;
}

export interface FleetOverview {
  tiles: {
    enrolled: number;
    seen24h: number;
    notSeen72h: number;
    onCurrentPct: number | null;
    crashes7d: number; webview2Failures7d: number; signInFailures7d: number;
    mcpOff: number; policyBehind: number; connectedNow: number;
  };
  adoption: Array<{ ring: Ring | 'unknown'; current: string | null; versions: Array<{ version: string; pcs: number; sharePct: number; isCurrent: boolean }> }>;
  adoptionSpark: Array<number | null>;
  problems: Array<{ kind: FleetProblemKind; pcs: number }>;
  latestCritical: Array<{ deviceId: string; computerName: string | null; atMs: number; category: string; message: string }>;
  mcpServers: FleetMcpSummary[];
  sourcesFailed: string[];
}

export interface FleetDeviceRow {
  deviceId: string; computerName: string | null; ownerEmail: string | null;
  agentVersion: string | null; versionChip: 'current' | 'behind' | 'unknown'; ring: Ring | null;
  status: FleetStatus; lastSeenAt: number | null;
  crashes7d: number; webview2Failures7d: number; signInFailures7d: number;
  mcpOff: boolean; quarantined: boolean; disabled: boolean;
  policy: { applied: number; current: number } | null;
  push: { connectedNow: boolean; drops24h: number | null };
  problems: FleetProblemKind[];
}

export interface FleetDeviceDayPoint { day: string; errors: number; crashes: number; pushDrops: number; signInFailures: number; webview2Failures: number }

export interface FleetVersionRow {
  version: string; pcs: number; sharePct: number;
  rings: Record<Ring | 'unknown', number>;
  crashesPerPc7d: number; webview2PerPc7d: number;
  firstSeen: string; lastSeen: string;
  spark: number[];
  isCurrentFor: Ring[];
}

export interface FleetProblemRow {
  kind: FleetProblemKind; pcs: number;
  spark: number[];
  recent: Array<{ deviceId: string; computerName: string | null; lastSeenAt: number | null }>;
}

/** Every Fleet list: Pulse's `{ items, next, total }` (so screens/pulse/common.tsx usePagedList fits) and the sources that failed. */
export interface FleetList<T> { items: T[]; next: string | null; total: number; sourcesFailed: string[] }

export interface FleetDeviceAnswer { record: FleetRecord; days: FleetDeviceDayPoint[]; sourcesFailed: string[] }

/** GET /v1/admin/desktop/logs?device=<id>&limit=120 (desktopLogs.ts): the PC's ring, newest last. */
export interface DeviceLogsAnswer { deviceId: string; version?: string; user?: string; updatedAtMs: number; entries: DesktopLogEntry[] }

export type FleetQuery = Record<string, string | number | boolean | null | undefined>;

// ------------------------------------------------------------------------------------------------ calls

export const fleetApi = {
  settings: () => adminGet<FleetSettingsView>('/v1/admin/fleet/settings'),
  saveSettings: (patch: FleetSettingsPatch) => adminSend<FleetSettingsView>('/v1/admin/fleet/settings', { method: 'PUT', body: JSON.stringify(patch) }),
  overview: (ring?: string) => adminGet<FleetOverview>(`/v1/admin/fleet/overview${queryOf({ ring })}`),
  devices: (query: FleetQuery = {}) => adminGet<FleetList<FleetDeviceRow>>(`/v1/admin/fleet/devices${queryOf(query)}`),
  device: (deviceId: string) => adminGet<FleetDeviceAnswer>(`/v1/admin/fleet/devices/${encodeURIComponent(deviceId)}`),
  versions: (query: FleetQuery = {}) => adminGet<FleetList<FleetVersionRow>>(`/v1/admin/fleet/versions${queryOf(query)}`),
  problems: () => adminGet<{ items: FleetProblemRow[]; sourcesFailed: string[] }>('/v1/admin/fleet/problems'),
  /** The drawer's last 120 log entries: the existing desktop logs route (404 `unknown_device` when the ring holds nothing). */
  deviceLogs: (deviceId: string) => adminGet<DeviceLogsAnswer>(`/v1/admin/desktop/logs${queryOf({ device: deviceId, limit: 120 })}`),
};

export type FleetExportList = 'devices' | 'versions' | 'problems';

/** The filters each export takes (section 5: "that list's filters"); anything else is refused by the route. */
const EXPORT_FILTERS: Record<FleetExportList, readonly string[]> = {
  devices: ['status', 'version', 'ring', 'problem', 'q', 'showGone'],
  versions: ['ring'],
  problems: [],
};

/** `GET /v1/admin/fleet/export?list=…` with that list's filters as shown (no sort, cursor or limit: the export runs in key order). */
export function fleetExportHref(list: FleetExportList, filters: FleetQuery = {}): string {
  const params = new URLSearchParams({ list });
  for (const key of EXPORT_FILTERS[list]) {
    const value = filters[key];
    if (value === undefined || value === null || value === '' || value === false) continue;
    params.set(key, value === true ? '1' : String(value));
  }
  return `/v1/admin/fleet/export?${params.toString()}`;
}

// ------------------------------------------------------------------------------------------------ words people read (1.4, 12.2)

export type ChipTone = 'ok' | 'warn' | 'bad' | 'info' | 'neutral' | 'accent';
export interface FleetChip { tone: ChipTone; label: string; title?: string; plain?: boolean }

/** Status chips: Online ok, Recent neutral, Stale warning, Not seen bad, Gone muted (a plain chip, no dot). */
export const STATUS_CHIPS: Record<FleetStatus, FleetChip> = {
  online: { tone: 'ok', label: 'Online', title: 'connected (push stream)' },
  recent: { tone: 'neutral', label: 'Recent' },
  stale: { tone: 'warn', label: 'Stale' },
  'not-seen': { tone: 'bad', label: 'Not seen' },
  gone: { tone: 'neutral', label: 'Gone', plain: true },
};

export function statusChip(status: string | null | undefined): FleetChip {
  return STATUS_CHIPS[status as FleetStatus] ?? { tone: 'neutral', label: status || 'Unknown' };
}

export const PROBLEM_LABELS: Record<FleetProblemKind, string> = {
  'behind-version': 'Behind version',
  crashed: 'Crashed',
  'webview2-failing': 'WebView2 failing',
  'mcp-off': 'MCP off',
  'sign-in-failing': 'Sign-in failing',
  'not-seen': 'Not seen',
  'push-flapping': 'Push flapping',
  'policy-behind': 'Policy behind',
  quarantined: 'Quarantined',
  disabled: 'Gyro disabled',
  'update-refused': 'Update refused',
  'update-ineligible': 'Update ineligible',
  'version-disagrees': 'Versions disagree',
};

/** One line per problem kind: its rule (section 4.5), in words. */
export const PROBLEM_RULES: Record<FleetProblemKind, string> = {
  'behind-version': "Runs an older version than its ring's current version.",
  crashed: 'A crash dump or a critical entry arrived in the last 7 days, or its last health report counts crashes.',
  'webview2-failing': 'Its chat window (WebView2) failed to start, its renderer failed, or recreating it ran out of tries in the last 7 days.',
  'mcp-off': 'A Local connections switch is off for this PC.',
  'sign-in-failing': 'A sign-in failure arrived from this PC in the last 24 hours.',
  'not-seen': 'Not seen for longer than the stale limit (72 hours unless changed).',
  'push-flapping': 'Its push stream dropped more often in 24 hours than the setting allows (12 unless changed).',
  'policy-behind': 'Still runs an older PC switches revision an hour after the policy was saved (the minutes are a setting).',
  quarantined: 'At least one switch is held by quarantine.',
  disabled: 'Gyro is switched off on this PC.',
  'update-refused': 'The PC refused the update it was last offered.',
  'update-ineligible': 'Behind its ring and the OTA gate would refuse an update for a reason other than a stale health report.',
  'version-disagrees': 'A later report names an older version than an earlier one.',
};

const PROBLEM_TONES: Partial<Record<FleetProblemKind, ChipTone>> = {
  crashed: 'bad', 'webview2-failing': 'bad', 'sign-in-failing': 'bad', 'not-seen': 'bad', disabled: 'bad', 'update-refused': 'bad',
};

export function problemChip(kind: string): FleetChip {
  return { tone: PROBLEM_TONES[kind as FleetProblemKind] ?? 'warn', label: PROBLEM_LABELS[kind as FleetProblemKind] ?? kind, plain: true };
}

/** The OTA gate's reasons as sentences (closed; section 12.2). */
export const ELIGIBILITY_SENTENCES: Record<OtaIneligibilityReason, string> = {
  health_missing: 'No health report from this PC yet.',
  health_stale: 'Its last health report is older than 15 minutes; the next report clears this.',
  unhealthy: 'Its last health report says it is unhealthy.',
  crash_count_nonzero: 'It reported crashes since its last start.',
  ring_ineligible: 'Its ring does not take this release.',
  installed_version_invalid: 'Its installed version could not be read.',
  not_an_upgrade: 'It already runs this version or a newer one.',
  not_a_rollback: 'A rollback needs a PC on a newer version.',
  below_min_from_version: 'It is older than the oldest version this release updates from.',
};

export function eligibilitySentence(reason: string): string {
  return ELIGIBILITY_SENTENCES[reason as OtaIneligibilityReason] ?? reason;
}

export const VERSION_CHIPS: Record<FleetDeviceRow['versionChip'], FleetChip> = {
  current: { tone: 'ok', label: 'Current' },
  behind: { tone: 'warn', label: 'Behind' },
  unknown: { tone: 'neutral', label: 'Unknown' },
};

export const RING_LABELS: Record<Ring | 'unknown', string> = { canary: 'Canary', early: 'Early', broad: 'Broad', unknown: 'Unknown' };

export function ringLabel(ring: string | null | undefined): string {
  return RING_LABELS[(ring ?? 'unknown') as Ring | 'unknown'] ?? ring ?? 'Unknown';
}

export const MCP_CHIPS: Record<McpStatus, FleetChip> = {
  healthy: { tone: 'ok', label: 'Healthy' },
  unhealthy: { tone: 'bad', label: 'Unhealthy' },
  unknown: { tone: 'neutral', label: 'Unknown' },
  disabled: { tone: 'neutral', label: 'Disabled' },
  blocked: { tone: 'warn', label: 'Blocked' },
  'consent-required': { tone: 'warn', label: 'Consent required' },
  unsupported: { tone: 'neutral', label: 'Unsupported' },
  stale: { tone: 'warn', label: 'Stale' },
};

export function mcpChip(status: string): FleetChip {
  return MCP_CHIPS[status as McpStatus] ?? MCP_CHIPS.unknown;
}

/** A local connection's name as people read it: the name the service sends (it maps Gyro AI for Revit's keys), else its key. */
export function mcpName(server: Pick<FleetMcpServer, 'server' | 'displayName'>): string {
  return server.displayName?.trim() || server.server;
}

export const VERSION_SOURCE_LABELS: Record<VersionSource, string> = { health: 'health report', switches: 'PC switches report', logs: 'log upload' };

export const SWITCH_SOURCE_LABELS: Record<SwitchSource, string> = {
  server: 'Gyro Admin', machine: 'Intune on the PC', default: 'default', quarantine: 'quarantine', 'user-pause': 'paused by the person',
};

/** The names of the sources a read could not reach (section 4.1 `sourcesFailed`). */
export const SOURCE_LABELS: Record<string, string> = {
  registry: 'the PC registry', health: 'the health reports', switches: 'the PC switches reports', logs: 'the log entries',
  presence: 'the push presence', ota: 'the OTA releases', fleet: "Fleet health's own records",
};

/** "Some sources could not be read: the PC registry and the log entries. Their columns are blank." or null. */
export function sourcesFailedText(sources: readonly string[] | null | undefined): string | null {
  if (!sources || sources.length === 0) return null;
  const names = sources.map((source) => SOURCE_LABELS[source] ?? source);
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `Some sources could not be read just now: ${list}. What they hold is blank until the next read.`;
}

export const FLEET_JOB_LABELS: Record<FleetJobKey, string> = {
  tick: 'Every minute', snapshot: '15-minute snapshot', 'ota-scan': 'OTA scan', sweep: 'Retention sweep', alerts: 'Alert check',
};

/** Log categories the drawer's filter always offers, then the ones present (section 12.2 item 8). */
export const LOG_CATEGORIES = ['Gyro.Agent.WebView2', 'Gyro.SignIn', 'Gyro.Agent.Crash'] as const;

export function logCategoryOptions(entries: readonly DesktopLogEntry[]): string[] {
  const present = [...new Set(entries.map((entry) => entry.category))].filter((category) => !(LOG_CATEGORIES as readonly string[]).includes(category)).sort();
  return [...LOG_CATEGORIES, ...present];
}

/** The entries shown, as text to copy: one line each, the time in UTC, the level, the category, the message (and exception). */
export function logsText(entries: readonly DesktopLogEntry[]): string {
  return entries.map((entry) => {
    const at = Number.isFinite(entry.atMs) ? new Date(entry.atMs).toISOString() : '-';
    const exception = entry.exception ? `\n    ${entry.exception.replace(/\n/g, '\n    ')}` : '';
    return `${at} ${entry.level.toUpperCase()} ${entry.category} [${entry.source}] ${entry.message}${exception}`;
  }).join('\n');
}

// ------------------------------------------------------------------------------------------------ figures and times

/** `just now`, `5 min ago`, `3 h ago`, `2 days ago`; an em dash for nothing. */
export function ageText(at: number | null | undefined, now = Date.now()): string {
  if (typeof at !== 'number' || !Number.isFinite(at)) return '—';
  const ms = Math.max(0, now - at);
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)} min ago`;
  if (ms < 48 * 3_600_000) return `${Math.floor(ms / 3_600_000)} h ago`;
  return `${Math.floor(ms / 86_400_000)} days ago`;
}

/** "Online", "4 drops in 24 h" or "Not reported" (an agent that does not report pushDrops). */
export function pushText(push: { connectedNow: boolean; drops24h: number | null }): string {
  if (push.connectedNow) return 'Online';
  if (push.drops24h === null) return 'Not reported';
  return `${push.drops24h.toLocaleString('en-US')} drop${push.drops24h === 1 ? '' : 's'} in 24 h`;
}

/** `41/42`: the PC switches revision applied, of the current one. */
export function policyText(policy: { applied: number; current: number } | null): string {
  return policy ? `${policy.applied}/${policy.current}` : '—';
}

export function policyBehind(policy: { applied: number; current: number } | null): boolean {
  return Boolean(policy && policy.applied < policy.current);
}

/** A key's fingerprint as the drawer shows it: the first 12 characters. */
export function keyShort(fingerprint: string): string {
  return fingerprint.slice(0, 12);
}

/** A share from 0 to 100 as `43.7 %`. */
export function pctText(value: number | null | undefined, digits = 1): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${value.toFixed(digits)} %`;
}

/** Each effective switch with its label, value in words, source and whether it differs from everyone (the drawer's Switches). */
export function switchRows(switches: NonNullable<FleetRecord['switches']>): Array<{ key: string; label: string; value: string; source: string; differs: boolean }> {
  return Object.keys(switches.effective).sort().map((key) => {
    const entry = FALLBACK_CATALOG.find((item) => item.key === key);
    const value = switches.effective[key];
    const source = switches.sources[key];
    return {
      key,
      label: entry?.label ?? key,
      value: entry ? valueText(entry, value) : Array.isArray(value) ? value.join(', ') : String(value),
      source: source ? SWITCH_SOURCE_LABELS[source] ?? source : '—',
      differs: switches.differsFromFleet.includes(key),
    };
  });
}

// ------------------------------------------------------------------------------------------------ sort

export interface SortState { key: string; direction: 'asc' | 'desc' }

/** The route's `sort`: the key, `-` first for descending. */
export function sortParam(sort: SortState): string {
  return sort.direction === 'desc' ? `-${sort.key}` : sort.key;
}

export function parseSort(value: string | null | undefined, fallback: SortState): SortState {
  if (!value) return fallback;
  return value.startsWith('-') ? { key: value.slice(1), direction: 'desc' } : { key: value, direction: 'asc' };
}

/** A click on `key`: the same key flips; another key starts descending for a number or a time, ascending for words. */
export function toggleSort(current: SortState, key: string, numeric: boolean): SortState {
  if (current.key === key) return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  return { key, direction: numeric ? 'desc' : 'asc' };
}

// ------------------------------------------------------------------------------------------------ the settings form (section 6)

export type FleetNumberKey = 'recentHours' | 'staleHours' | 'goneDays' | 'deviceRetentionDays' | 'pushFlappingDrops' | 'policyBehindMinutes'
  | 'alert.crashesPerPc7d' | 'alert.minPcsPerVersion' | 'alert.darkPcs';

export interface FleetNumberField { key: FleetNumberKey; label: string; help: string; min: number; max: number; whole: boolean }

export const FLEET_NUMBER_FIELDS: readonly FleetNumberField[] = [
  { key: 'recentHours', label: 'Recent for (hours)', help: 'A PC last seen within this many hours reads Recent; after it, Stale. 13 unless changed; 1 to 48.', min: 1, max: 48, whole: true },
  { key: 'staleHours', label: 'Not seen after (hours)', help: 'A PC last seen longer ago than this reads Not seen. 72 unless changed; more than Recent, at most 720.', min: 1, max: 720, whole: true },
  { key: 'goneDays', label: 'Gone after (days)', help: 'A PC not seen for this many days reads Gone and leaves the lists. 30 unless changed; 7 to 365.', min: 7, max: 365, whole: true },
  { key: 'deviceRetentionDays', label: 'Keep a PC’s daily rows for (days)', help: '180 unless changed; 30 to 365.', min: 30, max: 365, whole: true },
  { key: 'pushFlappingDrops', label: 'Push flapping above (drops in 24 hours)', help: 'More drops than this in 24 hours is Push flapping. 12 unless changed; 1 to 1,000.', min: 1, max: 1000, whole: true },
  { key: 'policyBehindMinutes', label: 'Policy behind after (minutes)', help: 'How long after a PC switches save a PC may still run the older revision. 60 unless changed; 5 to 1,440.', min: 5, max: 1440, whole: true },
  { key: 'alert.crashesPerPc7d', label: 'Crash rate alert (crashes per PC in 7 days)', help: '0.5 unless changed; 0.01 to 100.', min: 0.01, max: 100, whole: false },
  { key: 'alert.minPcsPerVersion', label: 'Smallest version counted (PCs)', help: 'A version needs at least this many PCs before its crash rate counts. 5 unless changed; 1 to 1,000.', min: 1, max: 1000, whole: true },
  { key: 'alert.darkPcs', label: 'Gone dark alert (PCs)', help: 'This many PCs reading Not seen within the last 7 days raises it. 5 unless changed; 1 to 10,000.', min: 1, max: 10000, whole: true },
];

export const NOTIFY_IT_HELP = 'Nothing is sent yet: alerts to IT need a route through The Bridge (the-bridge#894). With this on, Gyro records in the audit log each alert it would have sent.';

/** The form: numbers typed as text (a half-typed value is kept), overrides as text ('' for none). */
export type FleetSettingsForm = Record<FleetNumberKey, string> & { override: Record<Ring, string>; notifyIt: boolean };

function numberOf(settings: FleetSettings, key: FleetNumberKey): number {
  if (key.startsWith('alert.')) return settings.alert[key.slice(6) as keyof FleetSettings['alert']];
  return settings[key as Exclude<FleetNumberKey, `alert.${string}`>];
}

export function fleetForm(settings: FleetSettings): FleetSettingsForm {
  const form = { override: { canary: '', early: '', broad: '' }, notifyIt: settings.notifyIt } as FleetSettingsForm;
  for (const field of FLEET_NUMBER_FIELDS) form[field.key] = String(numberOf(settings, field.key));
  for (const ring of RINGS) form.override[ring] = settings.currentVersionOverride?.[ring] ?? '';
  return form;
}

const VERSION_PATTERN = /^\d+\.\d+\.\d+(\.\d+)?$/;

/** The first thing the service would refuse, in words, or null. */
export function validateFleet(form: FleetSettingsForm): string | null {
  const read: Partial<Record<FleetNumberKey, number>> = {};
  for (const field of FLEET_NUMBER_FIELDS) {
    const text = form[field.key].trim();
    const value = Number(text);
    if (!text || !Number.isFinite(value) || (field.whole && !Number.isInteger(value)) || value < field.min || value > field.max) {
      return `${field.label}: ${field.whole ? 'a whole number' : 'a number'} from ${field.min.toLocaleString('en-US')} to ${field.max.toLocaleString('en-US')}.`;
    }
    read[field.key] = value;
  }
  if ((read.staleHours ?? 0) <= (read.recentHours ?? 0)) return 'Not seen after (hours) must be more than Recent for (hours).';
  if ((read.goneDays ?? 0) * 24 <= (read.staleHours ?? 0)) return 'Gone after (days) must be longer than Not seen after (hours).';
  for (const ring of RINGS) {
    const text = form.override[ring].trim();
    if (text && !VERSION_PATTERN.test(text)) return `${RING_LABELS[ring]} current version: a dotted version such as 0.4.12.0, or empty.`;
  }
  return null;
}

/** Only what changed, nested objects key by key (the PUT's strict partial patch). */
export function fleetPatch(form: FleetSettingsForm, stored: FleetSettings): FleetSettingsPatch {
  const patch: FleetSettingsPatch = {};
  for (const field of FLEET_NUMBER_FIELDS) {
    const value = Number(form[field.key].trim());
    if (!form[field.key].trim() || !Number.isFinite(value) || value === numberOf(stored, field.key)) continue;
    if (field.key.startsWith('alert.')) patch.alert = { ...patch.alert, [field.key.slice(6)]: value };
    else (patch as Record<string, unknown>)[field.key] = value;
  }
  for (const ring of RINGS) {
    const next = form.override[ring].trim() || null;
    if (next !== (stored.currentVersionOverride?.[ring] ?? null)) patch.currentVersionOverride = { ...patch.currentVersionOverride, [ring]: next };
  }
  if (form.notifyIt !== stored.notifyIt) patch.notifyIt = form.notifyIt;
  return patch;
}

/** The changed keys as the audit row names them (`keys=recentHours,currentVersionOverride.early`), for the saved line. */
export function patchKeys(patch: FleetSettingsPatch): string[] {
  const keys: string[] = [];
  for (const [key, value] of Object.entries(patch)) {
    if (value !== null && typeof value === 'object') for (const inner of Object.keys(value)) keys.push(`${key}.${inner}`);
    else keys.push(key);
  }
  return keys.sort();
}

// ------------------------------------------------------------------------------------------------ the probe (section 12.1)

/** The probe's answer: a 404 (`fleet_disabled`), 401 or 403 hides the page; any other failure keeps it (it says what failed). */
export function fleetVisible(probeError: unknown): boolean {
  if (probeError === null || probeError === undefined) return true;
  const status = statusOf(probeError);
  return !(status === 404 || status === 401 || status === 403);
}

/** The page's state: `checking` until the probe answers (a deep link waits instead of flashing "not switched on"). */
export function fleetNav(consoleRole: boolean, probe: boolean | null): 'checking' | 'shown' | 'hidden' {
  if (!consoleRole) return 'hidden';
  if (probe === null) return 'checking';
  return probe ? 'shown' : 'hidden';
}

/** GET /v1/admin/fleet/settings, once per console session for an admin or auditor (the Pulse probe's pattern). */
export function useFleetNav(consoleRole: boolean): 'checking' | 'shown' | 'hidden' {
  const [probe, setProbe] = useState<boolean | null>(null);
  useEffect(() => {
    if (!consoleRole) return;
    let live = true;
    setProbe(null);
    fleetApi.settings().then(
      () => { if (live) setProbe(true); },
      (error: unknown) => { if (live) setProbe(fleetVisible(error)); },
    );
    return () => {
      live = false;
    };
  }, [consoleRole]);
  return fleetNav(consoleRole, probe);
}

/** A failed read or save in words: the dark service, a refused write, storage that is down, else the service's own words. */
export function fleetFailure(error: unknown, what = 'The Fleet health data could not be read.'): string {
  const status = statusOf(error);
  if (status === 404 && (error as { message?: unknown }).message === 'fleet_disabled') return 'Fleet health is switched off: the service answers 404 until the fleet flag is on under Feature flags.';
  if (status === 403) return 'Only IT admins can make this change.';
  if (status === 503) return 'Fleet health storage is unavailable just now. Try again in 30 seconds.';
  return errorText(error, what);
}
